import axios from 'axios'
import 'bootstrap'
import { touchAll, touchElement } from 'lambda-dom'
import { not, prop } from 'ramda'
import { BehaviorSubject, debounceTime } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'

import '@app-bootstrappers/site-header.bootstrapper'
import '@app-bootstrappers/image-containers.bootstrapper'
import { windowWidth$ } from '@app-bootstrappers/window-size-tracking.bootstrapper'
import { youtubeApiP } from '@app-bootstrappers/youtube-api.bootstrapper'
import { haveClassIf, haveClassUnless } from '@app-helpers'
import { autoSearch } from '@app-services'
import { articleId, counter, grid } from './newsroom-detail.bootstrap'
import { closeVideo, playVideo, resizeRelevantGrid, writeTextToClipboard } from './newsroom-detail.lib'

// ------------------------------------------------------------------------------
//      Initialize the contact form
// ------------------------------------------------------------------------------

autoSearch()

// ------------------------------------------------------------------------------
//      Toggle between latest and most-read articles in the sidebar
// ------------------------------------------------------------------------------

touchAll([
    '#article-toggle',
    '#most-read-label',
    '#latest-label',
    '#toggle-content',
    '#most-read',
    '#latest',
], (
    toggle: HTMLElement,
    mostReadLabel: HTMLElement,
    latestLabel: HTMLElement,
    toggleContainer: HTMLElement,
    mostReadContainer: HTMLElement,
    latestContainer: HTMLElement,
) => {
    const flipped$ = new BehaviorSubject(false)

    const toggleMode = (): void => setFlipped(not(flipped$.getValue()))
    const setFlipped = (flipped: boolean) => flipped$.next(flipped)

    toggle.addEventListener('click', toggleMode)
    mostReadLabel.addEventListener('click', () => setFlipped(false))
    latestLabel.addEventListener('click', () => setFlipped(true))

    flipped$.pipe(distinctUntilChanged()).subscribe((flipped) => {
        haveClassUnless(flipped, mostReadLabel, 'active')
        haveClassIf(flipped, toggle, 'active')
        haveClassIf(flipped, latestLabel, 'active')
        haveClassIf(flipped, toggleContainer, 'flipped')
        haveClassUnless(flipped, mostReadContainer, 'active')
        haveClassIf(flipped, latestContainer, 'active')
    })
})

// ------------------------------------------------------------------------------
//      Handle article voting
// ------------------------------------------------------------------------------

async function updateArticleClap(): Promise<any> {
    return axios.post('/progress/article-votes', { articleId })
        .then(prop('data'))
}

function handleArticleClaps(): void {
    const clap = document.querySelector('.article__share-bar__right')

    if (! clap
        || ! (clap instanceof HTMLDivElement)
        || ! counter
        || ! (counter instanceof HTMLParagraphElement)
    ) return

    let upvoteCountStorage = counter.innerHTML
    let animationInProgress = false

    clap.addEventListener('click', () => {
        if (animationInProgress) return

        updateArticleClap().then((response) => {
            if (response.upvoteCount) {
                counter.classList.remove('hover-state')
                counter.classList.add('clicked')
                animationInProgress = true

                setTimeout(() => {
                    counter.classList.remove('clicked')
                    counter.innerHTML = response.upvoteCount
                    upvoteCountStorage = counter.innerHTML
                    animationInProgress = false
                }, 1000)
            }
        })
    })

    clap.addEventListener('mouseover', () => {
        if (animationInProgress) {
            return
        }

        counter.classList.add('hover-state')
        counter.innerHTML = '+1'
    })

    clap.addEventListener('mouseout', () => {
        if (animationInProgress) {
            return
        }

        counter.classList.remove('hover-state')
        counter.innerHTML = upvoteCountStorage
    })

    clap.addEventListener('mouseleave', () => {
        if (animationInProgress) {
            return
        }

        counter.classList.remove('hover-state')
        counter.innerHTML = upvoteCountStorage
    })
}

handleArticleClaps()

// ------------------------------------------------------------------------------
//      Article youtube player
// ------------------------------------------------------------------------------

youtubeApiP.then(() => touchAll([
    '#youtube-video-modal',
    '#article-video-toggle',
    '#article-youtube-video',
    '.yvm--close',
], (
    modal: HTMLElement,
    toggleButton: HTMLButtonElement,
    videoIframe: HTMLIFrameElement,
    closeButton: HTMLButtonElement,
) => {
    const player = new YT.Player(videoIframe, {
        events: {
            onStateChange(event) {
                if (event.data === YT.PlayerState.ENDED) {
                    closeVideo(modal, event.target)
                }
            },
            onReady() {
                toggleButton.addEventListener('click', () => playVideo(modal, player))
                closeButton.addEventListener('click', () => closeVideo(modal, player))
            },
        },
    })
}))

// ------------------------------------------------------------------------------
//      Go back link handling
// ------------------------------------------------------------------------------

touchElement('.article__back', (goBackButton: HTMLAnchorElement) => {
    goBackButton.href = '/progress'
})

// ------------------------------------------------------------------------------
//      Copy link handling
// ------------------------------------------------------------------------------

touchElement('.article__share-bar__copy-link', (copyLinkButton: HTMLAnchorElement) => {
    copyLinkButton.addEventListener('click', async (event) => {
        event.preventDefault()

        if (location.href) {
            await writeTextToClipboard(location.href)
        }
    })
})

// ------------------------------------------------------------------------------
//      Relevant grid handling
// ------------------------------------------------------------------------------

if (grid && grid instanceof HTMLElement) {
    resizeRelevantGrid(grid)
}

windowWidth$.pipe(
    debounceTime(250),
).subscribe({
    next: () => {
        if (grid && grid instanceof HTMLElement) resizeRelevantGrid(grid)
    },
})
